import { getApiBase } from '../api.js';

export const API_BASE = () => `${getApiBase()}/api`;
// MQTT WebSocket URL is now per-tab and derived from /api/mqtt-token's
// response (see useFleetMapMqtt.js). MQTT_WS_URL constant + the
// MQTT_LIVE_REPORT_TOPIC dead reference removed in A2.4 — they pointed
// at the now-closed wss://mqtt.<base>/ anonymous endpoint (C1 security
// finding, closed 2026-05-27 in commit d52a23c7).
export const MQTT_REALTIME_TOPIC = 'ovr/+/realtime';
export const POLL_INTERVALS = [30, 60, 120, 300, 300, 300, 300, 300];

// Live report cadence (plan 2026-10-11 cloud ingestion backpressure, KTD11,
// R9, R10). On cloud the server computes the live report once a minute per
// window and shares it, so the map polls once a minute. Edge keeps 10 s.
// The stale note fires at twice the poll, so a figure is never called old
// on every other refresh, and never under a minute, because the edge poll
// doubled would call a 25 s figure old. A function of the host rather than
// a constant, because each hook derives isCloud from capabilities at render
// time and there is no env module.
export const liveReportCadence = (isCloud) => {
  const pollMs = isCloud ? 60_000 : 10_000;
  const staleSeconds = Math.max((2 * pollMs) / 1000, 60);
  const minutes = Math.round(staleSeconds / 60);
  const spelled = minutes === 1 ? 'a minute' : `${minutes === 2 ? 'two' : minutes} minutes`;
  return { pollMs, staleSeconds, staleNote: `The live report is older than ${spelled}.` };
};
export const MOVE_THRESHOLD_METERS = 20;
export const STALE_SECONDS = 15;
// Fallback for peers that don't advertise LWT presence (legacy nodes).
// Must exceed the publisher heartbeat (120s) with margin for jitter.
export const NODE_STALE_SECONDS = 180;
export const NODE_OFFLINE_SECONDS = 43200;

// --- Portal-fed presence ("Leyden Cloud") ---------------------------------
// The cloud portal tank collector writes system_registry rows under the
// reserved node id `portal`; /api/nodes groups each one as a `portal-<sid>`
// virtual node carrying source 'portal' (registry._build_nodes_from_registry).
export const PORTAL_SOURCE = 'portal';
// Sidebar identity for the single card all those virtual nodes collapse into.
export const PORTAL_CARD_KEY = 'portal';
export const PORTAL_CARD_NAME = 'Leyden Cloud';

// Freshness thresholds for portal rows. These are the client half of a
// matched pair: cloud/services/api/node_pipeline.py holds the server half
// (PORTAL_DATA_STALE_SECONDS = 150, used by is_row_stale). Move one and the
// card disagrees with the `stale` flag /api/nodes ships for the same system.
//
// A portal row refreshes once per 60 s collector cycle and carries the
// VENDOR's data time rather than the write time, so it is already most of a
// cycle old when it lands. Under the edge numbers (STALE_SECONDS = 15) a
// perfectly healthy unit would read stale on every render. 150 s is 2.5
// cycles: two consecutive missed cycles before the map calls it a problem.
// The edge numbers do NOT move: 150 s of real edge silence is a fault the
// map exists to show.
export const PORTAL_STALE_SECONDS = 150;
// Offline is the vendor-outage bound: 15 min is the collector's own
// api_unreachable neighbourhood, far enough past stale to mean "gone", not
// "late". It is a VERDICT, not a hide: the card keeps its offline pill, and
// the marker survives to the separate 18 h map-hide bound
// (VRM_MAP_STALE_SEC in useNodeFiltering.js). The "hide offline nodes"
// toggle skips virtual nodes for the same reason, so 15 min of vendor
// silence never removes the only card a portal unit has.
export const PORTAL_OFFLINE_SECONDS = 900;

// --- ElectricFish MHV presence ("400Squared") -----------------------------
// The MHV cloud collector writes system_registry rows under the reserved node
// id `ef`, grouped as `ef-<sid>` virtual nodes carrying source 'mhv'. Client
// half of a matched pair: cloud/services/api/node_pipeline.py holds the server
// half (MHV_DATA_STALE_SECONDS = 300). The OCPP feed refreshes ~45 s and
// carries the vendor data time, so, like portal, the edge 15 s rule would flap
// a healthy unit. 300 s spans several missed poll cycles; offline 15 min is
// the same vendor-outage bound as portal.
export const MHV_SOURCE = 'mhv';
export const MHV_STALE_SECONDS = 300;
export const MHV_OFFLINE_SECONDS = 900;
export const ZOOM_DETAIL_THRESHOLD = 14; // >= 14: system pins, < 14: group markers

// Map chip cell floor (plan 2026-10-09-1408 KTD16, R3, R4). A PV, AC input
// or DC cell appears only while its value is at this many watts or more,
// so a solar trickle or a GX's own DC draw does not add a cell to most
// chips. On a bus-group chip the floor applies to each summed group value,
// not to each member (KTD7). No buffer around it: 49 W hides, 50 W shows.
export const CHIP_CELL_MIN_W = 50;
export const MAP_PROVIDER_STORAGE_KEY = 'ovr_fleet_map_provider_v1';
export const PLACEHOLDER_DEPLOYMENT_IDS = new Set(['__DEPLOYMENT_ID__', '__DEPLOYEMENT_ID__']);
export const WINDOW_ID_STORAGE_KEY = 'ovr_fleet_event_window_map_v1';
export const PRIMARY_EVENT_STORAGE_KEY = 'ovr_fleet_primary_event_v1';

// Sessions the cloud treats as read-only viewers: a client-portal login and
// an anonymous visitor. They never see internal alarm state and their
// location saves are refused, so the map skips the pipeline alarm poll and
// drag for them (plan 2026-10-09-1408 KTD3, KTD9).
export const READ_ONLY_ROLES = new Set(['client', 'anonymous']);

export const STATUS_RANK = {
  offline: -1,
  stale: -1,
  neutral: 0,
  ok: 1,
  warn: 2,
  bad: 3,
  alert: 4,
};
